import { Injectable } from '@angular/core';
import { IAdaptorService } from 'src/app/base/shared/syncfusion/adaptor-service.interface';
import { ConfigBasedAdminClient, FilterCondition, FilterDTO, FilterOperator, IConfigBasedStructuralEntryDTO, ParkingSubscriptionClient,
  ParkingSubscriptionDTO,
  ParkingTariffClient, ParkingTariffLimitedDTO, SortDirection, UpdateConfigBasedStructuralEntryDTO } from 'src/app/base/models/models.swagger.generated';
import { ClientService } from 'src/app/base/services/client/client.service';
import { Observable, of, map, forkJoin, tap, catchError } from 'rxjs';
import { ToastService } from 'src/app/base/services/toast.service';
import { IPagedItems } from 'src/app/base/shared/syncfusion/paged-items.interface';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ErrorType } from 'src/app/core/routing/error-types';
import { AdministrationItemType, DetailsInitInfo, IItemData, ITreeNodeData } from '../../data-structures';
import { ItemIdUtil } from '../../item-id.util';
import { ItemCache } from '../item-cache';

@Injectable({
  providedIn: 'root'
})
export class TreeViewConfigBasedService implements IAdaptorService {
  public itemCache = new ItemCache([AdministrationItemType.SubscriptionCollection, AdministrationItemType.ConfigBasedStructuralElementCollection, AdministrationItemType.TariffCollection]);
  public subscriptionCollectionId: string = null;
  private nodeIdCnt = 0;

  constructor(
    private configBasedAdminClient: ConfigBasedAdminClient,
    private clientService: ClientService,
    private tariffClient: ParkingTariffClient,
    private parkingSubscriptionClient: ParkingSubscriptionClient,
    private spinnerService: SpinnerService,
    private toastService: ToastService) { }

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<IPagedItems> {
    const clientId = this.clientService.selectedClient?.id;
    let parentType = null;
    let parentId: number = null;
    if (!filters) {
      this.itemCache.resetCache();
      filters = [];
      filters.push(new FilterDTO({
        isComplex: false,
        field: 'ParentId',
        operator: FilterOperator.IsNull,
      }));
    } else {
      const parentIdfilter = filters.find((filter) => filter.field === 'ParentId');
      if (parentIdfilter) {
        const parsedData = this.parseNodeId(parentIdfilter.value);
        parentIdfilter.value = parsedData.id?.toString();
        parentId = parsedData.id;
        if (parentIdfilter.value == null) {
          parentIdfilter.operator = FilterOperator.IsNull;
          this.itemCache.resetCache();
        }
        parentType = parsedData.type;
      }
    }
    if (parentType == null) {
      const mappedItems: IPagedItems = {items: [], totalNumberOfItems: 1};
      let id = this.getNextNodeId(null, AdministrationItemType.ConfigBasedStructuralElementCollection);
      mappedItems.items.push({
        id,
        hasChildren: true,
        parentId: null,
        expanded: true,
        itemData: {
          type: AdministrationItemType.ConfigBasedStructuralElementCollection,
          name: this.clientService.selectedClient?.name,
          inactive: false,
          dto: null
        }
      } as ITreeNodeData);
      this.subscriptionCollectionId = this.getNextNodeId(null, AdministrationItemType.SubscriptionCollection);
      mappedItems.items.push({
        id: this.subscriptionCollectionId,
        hasChildren: true,
        parentId: null,
        expanded: true,
        itemData: {
          type: AdministrationItemType.SubscriptionCollection,
          name: 'i18n.domain.administration.overview.unassigned-subscriptions',
          inactive: false,
          dto: null
        }
      } as ITreeNodeData);
      id = this.getNextNodeId(null, AdministrationItemType.TariffCollection);
      mappedItems.items.push({
        id,
        hasChildren: true,
        parentId: null,
        expanded: true,
        itemData: {
          type: AdministrationItemType.TariffCollection,
          name: 'i18n.domain.administration.overview.unassigned-tariffs',
          inactive: false,
          dto: null
        }
      } as ITreeNodeData);
      return of(mappedItems);
    }
    if (parentType == AdministrationItemType.ConfigBasedStructuralElementCollection) {
      return this.configBasedAdminClient.searchConfigBasedStructuralEntries(clientId, filters, 0, 0, 'name', SortDirection.Ascending).pipe(
        map((pagedItems) => {
          const mappedItems: IPagedItems = {items: [], totalNumberOfItems: pagedItems.totalNumberOfItems};
          pagedItems.items.forEach((item) => {
            let itemData: IItemData = {
              type: AdministrationItemType.ConfigBasedStructuralElement,
              name: item.name,
              inactive: false,
              dto: item
            };
            // the same subscription can be assigned to different structural elements and therefore its data has to be shared in the tree.
            itemData = this.itemCache.addOrUpdateItem(itemData).itemData;
            const mappedItem: ITreeNodeData = {
              id: this.getNextNodeId(item.id, AdministrationItemType.ConfigBasedStructuralElement),
              hasChildren: item.hasChildEntries || item.hasSubscriptions,
              itemData
            }
            mappedItems.items.push(mappedItem);
          });
          return mappedItems;
        })
      );
    }
    if (parentType == AdministrationItemType.ConfigBasedStructuralElement) {
      const entryObservable = this.configBasedAdminClient.searchConfigBasedStructuralEntries(clientId, filters, 0, 0, 'name', SortDirection.Ascending).pipe(
        map((pagedItems) => {
          const items = [];
          pagedItems.items.forEach((item) => {
            let itemData: IItemData = {
              type: AdministrationItemType.ConfigBasedStructuralElement,
              name: item.name,
              inactive: false,
              dto: item
            };
            // the same subscription can be assigned to different structural elements and therefore its data has to be shared in the tree.
            itemData = this.itemCache.addOrUpdateItem(itemData).itemData;
            const mappedItem: ITreeNodeData = {
              id: this.getNextNodeId(item.id, AdministrationItemType.ConfigBasedStructuralElement),
              hasChildren: item.hasChildEntries || item.hasSubscriptions,
              itemData
            }
            items.push(mappedItem);
          });
          return items;
        })
      );

      filters = [new FilterDTO({
        isComplex: false,
        field: 'StructuralEntryAssignments.StructuralEntryId',
        operator: FilterOperator.Equal,
        value: parentId?.toString()
      })];
      const subscriptionObservable = this.parkingSubscriptionClient.searchParkingSubscriptions(clientId, filters, 0, 0, 'name', SortDirection.Ascending).pipe(
        map((pagedItems) => {
          const items = [];
          pagedItems.items.forEach((item) => {
            let itemData: IItemData = {
              type: AdministrationItemType.Subscription,
              name: item.name,
              inactive: !item.active || !item.assignable,
              dto: item
            };
            // the same subscription can be assigned to different structural elements and therefore its data has to be shared in the tree.
            itemData = this.itemCache.addOrUpdateItem(itemData).itemData;
            const mappedItem: ITreeNodeData = {
              id: this.getNextNodeId(item.id, AdministrationItemType.Subscription),
              hasChildren: item.tariffs?.filter(value => !value.deleted).length > 0,
              itemData
            }
            items.push(mappedItem);
          });
          return items;
        }
      ));

      return forkJoin([subscriptionObservable, entryObservable]).pipe(
        map(input => {
          const mappedItems: IPagedItems = {items: [].concat(...input), totalNumberOfItems: 1};
          return mappedItems;
        })
      );
    }
    if (parentType == AdministrationItemType.SubscriptionCollection) {
      filters = [new FilterDTO({
        isComplex: false,
        field: 'StructuralEntryAssignments',
        operator: FilterOperator.None,
        value: null
      })];
      return this.parkingSubscriptionClient.searchParkingSubscriptions(clientId, filters, 0, 0, 'name', SortDirection.Ascending).pipe(
        map((pagedItems) => {
          const mappedItems: IPagedItems = {items: [], totalNumberOfItems: pagedItems.totalNumberOfItems};
          pagedItems.items.forEach((item) => {
            let itemData: IItemData = {
              type: AdministrationItemType.Subscription,
              name: item.name,
              inactive: !item.active || !item.assignable,
              dto: item
            };
            // the same subscription can be assigned to different structural elements and therefore its data has to be shared in the tree.
            itemData = this.itemCache.addOrUpdateItem(itemData).itemData;
            const mappedItem: ITreeNodeData = {
              id: this.getNextNodeId(item.id, AdministrationItemType.Subscription),
              hasChildren: item.tariffs?.filter(value => !value.deleted).length > 0,
              itemData
            }
            mappedItems.items.push(mappedItem);
          });
          return mappedItems;
        })
      );
    }
    if (parentType == AdministrationItemType.Subscription) {
      filters = [new FilterDTO({
          isComplex: false,
          field: 'SubscriptionId',
          operator: FilterOperator.Equal,
          value: parentId?.toString()
        })
      ];
      return this.tariffClient.searchParkingTariffs(clientId, filters, 0, 0, 'name', SortDirection.Ascending, false).pipe(
        map((pagedItems) => {
          const mappedItems: IPagedItems = {items: [], totalNumberOfItems: pagedItems.totalNumberOfItems};
          pagedItems.items.forEach((item) => {
            let itemData: IItemData = {
              type: AdministrationItemType.Tariff,
              name: item.name,
              inactive: !item.active,
              dto: item
            }
            // the same tariff can show up several time in the tree therefore its data has to be shared.
            itemData = this.itemCache.addOrUpdateItem(itemData).itemData;
            const mappedItem: ITreeNodeData = {
              id: this.getNextNodeId(item.id, AdministrationItemType.Tariff),
              hasChildren: false,
              itemData
            }
            mappedItems.items.push(mappedItem);
          });
          return mappedItems;
        })
      );
    }
    if (parentType == AdministrationItemType.TariffCollection) {
      filters = [new FilterDTO({
          isComplex: true,
          condition: FilterCondition.Or,
          predicates: [
            new FilterDTO({
              isComplex: false,
              field: 'SubscriptionId',
              operator: FilterOperator.IsNull
            }),
            new FilterDTO({
              isComplex: false,
              field: 'Subscription.Deleted',
              operator: FilterOperator.Equal,
              value: 'true'
            })
          ]
        })
      ];
      return this.tariffClient.searchParkingTariffs(clientId, filters, 0, 0, 'name', SortDirection.Ascending, false).pipe(
        map((pagedItems) => {
          const mappedItems: IPagedItems = {items: [], totalNumberOfItems: pagedItems.totalNumberOfItems};
          pagedItems.items.forEach((item) => {
            let itemData: IItemData = {
              type: AdministrationItemType.Tariff,
              name: item.name,
              inactive: !item.active,
              dto: item
            }
            // the same tariff can show up several time in the tree therefore its data has to be shared.
            itemData = this.itemCache.addOrUpdateItem(itemData).itemData;
            const mappedItem: ITreeNodeData = {
              id: this.getNextNodeId(item.id, AdministrationItemType.Tariff),
              hasChildren: false,
              itemData
            }
            mappedItems.items.push(mappedItem);
          });
          return mappedItems;
        })
      );
    }
    // default
    return of({items: [], totalNumberOfItems: 0});
  }

  public addRecord(data: any): Observable<any> {
    // this is just to make tree view happy, so it will show the node in the tree
    return of(data);
  }

  public updateRecord(data: any): Observable<any> {
    // this is just to make tree view happy, so it will show the node in the tree
    return of(data);
  }

  public deleteRecord(id: number): Observable<any> {
    // this is just to make tree view happy, so it will show the node in the tree
    return of(id);
  }

  public deleteStructuralEntry(id: number, sourceEntryId?: number): Observable<any> {
    this.spinnerService.show();
    return this.configBasedAdminClient.deleteConfigBasedStructuralEntry(id).pipe(
      tap(() => {
        const sourceEntry = this.itemCache.getCachedItem(sourceEntryId, AdministrationItemType.ConfigBasedStructuralElement);
        if (sourceEntry) {
          this.configBasedAdminClient.getConfigBasedStructuralEntry(sourceEntry.itemData.dto.id).subscribe((entry) => {
            sourceEntry.itemData.dto = entry;
            this.spinnerService.hide();
        this.toastService.showSuccess('i18n.domain.administration.overview.messages.structural-entry-delete-title', 'i18n.domain.administration.overview.messages.structural-entry-delete-success');
          });
        } else {
          this.spinnerService.hide();
        this.toastService.showSuccess('i18n.domain.administration.overview.messages.structural-entry-delete-title', 'i18n.domain.administration.overview.messages.structural-entry-delete-success');
        }
      }),
      catchError((error: any) => {
        this.spinnerService.hide();
        this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        return of(null);
      })
    );
  }

  public moveStructuralEntry(structuralEntryId: number, sourceParentId: number, targetParentId: number): Observable<void> {
    this.spinnerService.show();
    return this.configBasedAdminClient.updateConfigBasedStructuralEntry(new UpdateConfigBasedStructuralEntryDTO({id: structuralEntryId, parentId: targetParentId ?? 0})).pipe(
      tap(() => {
        // update target entry and source entry
        const targetEntry = this.itemCache.getCachedItem(targetParentId, AdministrationItemType.ConfigBasedStructuralElement);
        if (targetEntry) {
          (targetEntry.itemData.dto as IConfigBasedStructuralEntryDTO).hasChildEntries = true;
        }
        const sourceEntry = this.itemCache.getCachedItem(sourceParentId, AdministrationItemType.ConfigBasedStructuralElement);
        if (sourceEntry) {
          this.configBasedAdminClient.getConfigBasedStructuralEntry(sourceEntry.itemData.dto.id).subscribe((entry) => {
            sourceEntry.itemData.dto = entry;
            this.moveItemSuccess();
          });
        } else {
          this.moveItemSuccess();
        }
      }),
      catchError((error: any) => {
        return this.moveItemError();
      })
    );
  }

  public moveTariff(tariffId: number, targetSubscriptionId: number): Observable<void> {
    this.spinnerService.show();
    return this.tariffClient.assignNewSubscriptionToTariff(tariffId, targetSubscriptionId).pipe(
      tap(() => {
        const cachedItem = this.itemCache.getCachedItem(tariffId, AdministrationItemType.Tariff);
        const itemData = cachedItem?.itemData;
        if (itemData && itemData.type === AdministrationItemType.Tariff) {
          let parkingTariff = itemData.dto as ParkingTariffLimitedDTO;
          parkingTariff.subscriptionId = targetSubscriptionId;
        }
        this.moveItemSuccess();
      }),
      catchError((error: any) => {
        return this.moveItemError();
      })
    );
  }

  public deleteTariff(tariffId: number): Observable<void> {
    this.spinnerService.show();
    return this.tariffClient.deleteParkingTariff(tariffId).pipe(
      tap(() => {
        this.spinnerService.hide();
        this.toastService.showSuccess('i18n.domain.administration.overview.messages.tariff-delete-title', 'i18n.domain.administration.overview.messages.tariff-delete-success');
      }),
      catchError((error: any) => {
        this.spinnerService.hide();
        this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        return of(null);
      })
    );
  }

  public moveSubscriptionFromElement(subscriptionId: number, sourceEntryId?: number, targetEntryId?: number): Observable<ParkingSubscriptionDTO> {
    this.spinnerService.show();
    return this.parkingSubscriptionClient.moveToStructuralEntry(subscriptionId, sourceEntryId, targetEntryId).pipe(
      tap((dto) => {
        const cachedItem = this.itemCache.getCachedItem(subscriptionId, AdministrationItemType.Subscription);
        const itemData = cachedItem?.itemData;
        if (itemData) {
          itemData.dto = dto;
        }

        // update target entry and source entry
        const targetEntry = this.itemCache.getCachedItem(targetEntryId, AdministrationItemType.ConfigBasedStructuralElement);
        if (targetEntry) {
          (targetEntry.itemData.dto as IConfigBasedStructuralEntryDTO).hasSubscriptions = true;
        }

        const sourceEntry = this.itemCache.getCachedItem(sourceEntryId, AdministrationItemType.ConfigBasedStructuralElement);
        if (sourceEntry) {
          this.configBasedAdminClient.getConfigBasedStructuralEntry(sourceEntry.itemData.dto.id).subscribe((entry) => {
            sourceEntry.itemData.dto = entry;
            this.moveItemSuccess();
          });
        } else {
          this.moveItemSuccess();
        }
      }),
      catchError((error: any) => {
        return this.moveItemError();
      }));
  }

  public deleteSubscriptionFromElement(subscriptionId: number, sourceEntryId?: number): Observable<ParkingSubscriptionDTO> {
    this.spinnerService.show();
    if (sourceEntryId){
      return this.parkingSubscriptionClient.removeFromStructuralEntry(subscriptionId, sourceEntryId).pipe(
        tap((dto) => {
          const cachedItem = this.itemCache.getCachedItem(subscriptionId, AdministrationItemType.Subscription);
          const itemData = cachedItem?.itemData;
          if (itemData) {
            itemData.dto = dto;
          }

          const sourceEntry = this.itemCache.getCachedItem(sourceEntryId, AdministrationItemType.ConfigBasedStructuralElement);
          if (sourceEntry) {
            this.configBasedAdminClient.getConfigBasedStructuralEntry(sourceEntry.itemData.dto.id).subscribe((entry) => {
              sourceEntry.itemData.dto = entry;
              this.removeItemSuccess();
            });
          } else {
            this.removeItemSuccess();
          }
        }),
        catchError((error: any) => {
          return this.moveItemError();
        })
      );
    }
    return of(null);
  }

  public addSubscriptionToElement(subscriptionId: number, targetEntryId: number) {
    this.spinnerService.show();
    return this.parkingSubscriptionClient.addToStructuralEntry(subscriptionId, targetEntryId).pipe(
      map((dto) => {
        let itemData: IItemData = {
          type: AdministrationItemType.Subscription,
          name: dto.name,
          inactive: !dto.active || !dto.assignable,
          dto: dto
        };
        // the same subscription can be assigned to different structural elements and therefore its data has to be shared in the tree.
        itemData = this.itemCache.addOrUpdateItem(itemData).itemData;
        const mappedItem: ITreeNodeData = {
          id: this.getNextNodeId(dto.id, AdministrationItemType.Subscription),
          hasChildren: dto.tariffs?.filter(value => !value.deleted).length > 0,
          itemData
        }

        const targetEntry = this.itemCache.getCachedItem(targetEntryId, AdministrationItemType.ConfigBasedStructuralElement);
        if (targetEntry) {
          (targetEntry.itemData.dto as IConfigBasedStructuralEntryDTO).hasSubscriptions = true;
        }
        this.addItemSuccess();
        return mappedItem;
      }),
      catchError((error: any) => {
        return this.moveItemError();
      })
    );
  }

  public getNextNodeId(itemId: number, type: AdministrationItemType): string {
    this.nodeIdCnt++;
    return ItemIdUtil.createItemId(itemId, type, this.nodeIdCnt);
  }

  public parseNodeId(nodeId: string): DetailsInitInfo {
    return ItemIdUtil.parseItemId(nodeId) as DetailsInitInfo;
  }

  private addItemSuccess(): void {
    this.spinnerService.hide();
    this.toastService.showSuccess('i18n.domain.administration.overview.messages.item-added-title', 'i18n.domain.administration.overview.messages.item-added-success');
  }

  private removeItemSuccess(): void {
    this.spinnerService.hide();
    this.toastService.showSuccess('i18n.domain.administration.overview.messages.item-removed-title', 'i18n.domain.administration.overview.messages.item-removed-success');
  }

  private moveItemSuccess(): void {
    this.spinnerService.hide();
    this.toastService.showSuccess('i18n.domain.administration.overview.messages.item-moved-title', 'i18n.domain.administration.overview.messages.item-moved-success');
  }

  private moveItemError(): Observable<any> {
    this.spinnerService.hide();
    this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
    return of(null);
  }
}
